import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AccountDto, GenreId, ProviderId } from '@filmsloth/shared';
import { api, ApiError } from '../api/client.js';

interface AccountValue {
  account: AccountDto | null;
  loading: boolean;
  /** Convenience mirror of `account?.adFree` — the one thing most callers actually want. */
  adFree: boolean;
  /** True for an account trusted to settle facts on its own say-so. */
  moderator: boolean;
  /** The failure behind the most recent `login`/`register` call, or null. */
  error: ApiError | null;
  /** `identifier` is either the account's email or its username. */
  login: (identifier: string, password: string) => Promise<boolean>;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  /** Dismisses the error from a previous `login`/`register` attempt, e.g. once the form is edited again. */
  clearError: () => void;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Signed-in only. Updates the account's default providers/genres and the local copy alike. */
  updatePreferences: (
    preferredProviders: ProviderId[],
    preferredGenres: GenreId[],
  ) => Promise<boolean>;
}

const AccountContext = createContext<AccountValue | null>(null);

/**
 * Whoever this browser's cookie belongs to, if anyone.
 *
 * An account only matters for two things here — contributing to the
 * Filmwerkstatt and carrying ad-free credit — so a visitor with no cookie is
 * the ordinary, expected state, not an error. `/auth/me` answering
 * `{ account: null }` never throws, and neither does the initial load: a
 * network hiccup on mount just leaves the visitor looking signed out, which
 * is exactly how they would see the app anyway.
 */
export function AccountProvider({ children }: { children: ReactNode }) {
  const [account, setAccount] = useState<AccountDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      setAccount(result.account);
    } catch {
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AccountValue>(() => {
    // `login` and `register` share everything but which endpoint they hit,
    // and both need the same three outcomes wired up: the account lands in
    // state, the previous error clears, or the new error takes its place —
    // as an `ApiError` so the caller can switch on `.code` instead of
    // pattern-matching a message string meant for a different locale.
    const attempt = async (call: () => Promise<{ account: AccountDto }>) => {
      try {
        const result = await call();
        setAccount(result.account);
        setError(null);
        return true;
      } catch (cause) {
        setError(
          cause instanceof ApiError ? cause : new ApiError(0, 'unknown_error', 'Request failed'),
        );
        return false;
      }
    };

    return {
      account,
      loading,
      adFree: account?.adFree ?? false,
      moderator: account?.role === 'moderator',
      error,
      login: (identifier, password) => attempt(() => api.login(identifier, password)),
      register: (username, email, password) =>
        attempt(() => api.register(username, email, password)),
      clearError: () => setError(null),
      logout: async () => {
        // Cleared locally before the round trip returns: on a shared laptop
        // the safer failure mode is looking signed out when the request
        // secretly didn't land, not staying signed in when it did.
        setAccount(null);
        try {
          await api.logout();
        } catch {
          // The cookie is httpOnly and expires server-side either way; there
          // is nothing left here worth retrying or surfacing.
        }
      },
      refresh,
      updatePreferences: async (preferredProviders, preferredGenres) => {
        try {
          const result = await api.updatePreferences(preferredProviders, preferredGenres);
          setAccount(result.account);
          return true;
        } catch {
          return false;
        }
      },
    };
  }, [account, loading, error, refresh]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error('useAccount must be used inside <AccountProvider>');
  return value;
}
